import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, downloadSharedPdf, downloadSharedReferenceBriefPdf } from "@/lib/api";
import { CVPreview } from "@/components/CVPreview";
import { AdvisorCommentPanel } from "@/components/AdvisorCommentPanel";
import { AdvisorApprovalPanel } from "@/components/AdvisorApprovalPanel";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Eye, Languages } from "lucide-react";
import { REFBRIEF, bi } from "@/lib/i18n";
import { FileSignature, ClipboardCopy } from "lucide-react";

const UI = {
  tr: { advisorView: "Danışman Görünümü", loading: "Yükleniyor...", notFound: "Bağlantı bulunamadı veya geçersiz.", pdfTr: "PDF (TR)", pdfEn: "PDF (EN)", target: "Hedef" },
  en: { advisorView: "Advisor View", loading: "Loading...", notFound: "Link not found or invalid.", pdfTr: "PDF (TR)", pdfEn: "PDF (EN)", target: "Target" },
};

export default function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("dsa_lang") === "en" ? "en" : "tr");
  const [author, setAuthor] = useState(localStorage.getItem("dsa_advisor_name") || "");
  const u = UI[lang];

  useEffect(() => {
    api.get(`/shared/${token}`)
      .then((r) => { setData(r.data); setLang(r.data.cv.language === "en" ? "en" : "tr"); })
      .catch(() => setError(true));
  }, [token]);

  const addComment = (comment, key) =>
    setData((d) => ({ ...d, cv: { ...d.cv, comments: { ...(d.cv.comments || {}), [key]: [...((d.cv.comments || {})[key] || []), comment] } } }));

  const pdf = async (l) => {
    try { await downloadSharedPdf(token, l, `${(data.cv.personal?.fullName || "cv").replace(/\s+/g, "_")}_${l}.pdf`); }
    catch { toast.error("PDF error"); }
  };

  if (error) return <div data-testid="shared-not-found" className="min-h-screen flex items-center justify-center text-[#c0392b] font-semibold">{u.notFound}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#64748b]">{u.loading}</div>;

  const { cv, config } = data;
  return (
    <div className="min-h-screen bg-[#f4f6fa]">
      <header className="bg-[#1a2e4a] text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <img src="/brand/logo.png" alt="logo" className="h-9 w-9 object-contain" />
          <div className="min-w-0">
            <div className="font-display font-bold text-sm leading-tight truncate">Dual Study Abroad</div>
            <div data-testid="shared-student-name" className="text-[11px] text-[#c8d4e6] truncate">{cv.personal?.fullName || cv.title}</div>
          </div>
          <span data-testid="advisor-view-badge" className="ml-2 hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-[#c0392b] px-2.5 py-1"><Eye className="h-3 w-3" /> {u.advisorView}</span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="shared-lang-toggle" onClick={() => setLang((l) => (l === "tr" ? "en" : "tr"))} className="flex items-center gap-1 text-xs font-semibold rounded-full border border-white/30 px-2.5 py-1 hover:bg-white/10 transition-colors">
              <Languages className="h-3.5 w-3.5" /> {lang.toUpperCase()}
            </button>
            <Button data-testid="shared-pdf-tr" size="sm" onClick={() => pdf("tr")} className="bg-white/10 hover:bg-white/20 text-white h-8"><Download className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">{u.pdfTr}</span></Button>
            <Button data-testid="shared-pdf-en" size="sm" onClick={() => pdf("en")} className="bg-white/10 hover:bg-white/20 text-white h-8"><Download className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">{u.pdfEn}</span></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[1fr_420px] gap-6 items-start">
        <div className="overflow-x-auto thin-scroll min-w-0">
          <CVPreview cv={cv} config={config} lang={lang} />
        </div>
        <AdvisorCommentPanel token={token} cv={cv} config={config} lang={lang} onAdded={addComment} author={author} setAuthor={setAuthor} />
        <div className="lg:col-span-2">
          <AdvisorApprovalPanel token={token} cv={cv} lang={lang} author={author} onChange={(approval, valid) => setData((d) => ({ ...d, cv: { ...d.cv, approval, approvalValid: valid } }))} />
        </div>
        {(() => {
          const r = REFBRIEF[lang];
          const brief = bi(cv.statement?.referenceBrief, lang);
          const copy = async () => { try { await navigator.clipboard.writeText(brief); toast.success(r.copied); } catch { toast.error("Error"); } };
          const dl = async () => { try { await downloadSharedReferenceBriefPdf(token, lang, `referans_brifingi_${lang}.pdf`); } catch (e) { toast.error("Error"); } };
          return (
            <div data-testid="shared-reference-brief" className="lg:col-span-2 bg-white rounded-xl border border-[#e2e8f0] p-5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 font-display font-bold text-[#1a2e4a]"><FileSignature className="h-5 w-5 text-[#c0392b]" /> {r.title}</div>
                {brief && (
                  <div className="flex items-center gap-2">
                    <Button data-testid="shared-reference-brief-copy" size="sm" variant="outline" onClick={copy} className="border-[#1a2e4a] text-[#1a2e4a] h-8"><ClipboardCopy className="h-3.5 w-3.5 mr-1" /> {r.copy}</Button>
                    <Button data-testid="shared-reference-brief-pdf" size="sm" variant="outline" onClick={dl} className="border-[#c0392b] text-[#c0392b] h-8"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#64748b] mb-3">{r.forTeacher}</p>
              {brief
                ? <div className="text-[13.5px] text-[#334155] leading-relaxed whitespace-pre-wrap bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-4">{brief}</div>
                : <div className="text-sm text-[#94a3b8] italic">{r.empty}</div>}
            </div>
          );
        })()}
      </main>
    </div>
  );
}
